import { PLACEHOLDER_COMPANY_NAMES } from '../core/contracts.js'
import {
  companyNameMatchesDomain,
  isOwnSiteDomain,
  isOwnSiteUri,
} from '../core/intelligence/grounding-url.js'
import { extractedCompanyAlignsWithVisitor } from './identity-bound-sources.js'

const PLACEHOLDER_COMPANY_NAME_SET = new Set<string>(PLACEHOLDER_COMPANY_NAMES)

/**
 * Known domain-slug typos / alternate spellings → canonical slug used for research.
 * Prevents cold research against dead misspellings (e.g. bhakedemiet vs bhakademiet).
 * Fuzzy matching only runs against these canonical values — never arbitrary hosts.
 */
const DOMAIN_SLUG_ALIASES: Record<string, string> = {
  bhakedemiet: 'bhakademiet',
  bhakademiet: 'bhakademiet',
  bhakedemi: 'bhakademiet',
  bhakademi: 'bhakademiet',
  'bh-akademiet': 'bhakademiet',
  bhacademiet: 'bhakademiet',
  bhakademietno: 'bhakademiet',
}

function levenshtein(a: string, b: string): number {
  if (a === b) return 0
  if (!a.length) return b.length
  if (!b.length) return a.length
  const prev = new Array<number>(b.length + 1)
  const curr = new Array<number>(b.length + 1)
  for (let j = 0; j <= b.length; j++) prev[j] = j
  for (let i = 1; i <= a.length; i++) {
    curr[0] = i
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1
      curr[j] = Math.min((curr[j - 1] ?? 0) + 1, (prev[j] ?? 0) + 1, (prev[j - 1] ?? 0) + cost)
    }
    for (let j = 0; j <= b.length; j++) prev[j] = curr[j] ?? 0
  }
  return prev[b.length] ?? b.length
}

function extractDomainHost(hostOrDomain: string): string {
  let raw = hostOrDomain.trim().toLowerCase()
  if (!raw) return ''
  raw = raw.replace(/^[\s"'<(]+/, '').replace(/[\s"'>),]+$/, '')
  if (raw.includes('@') && !/^[a-z][a-z0-9+.-]*:\/\//i.test(raw)) {
    raw = raw.slice(raw.lastIndexOf('@') + 1)
  }
  raw = raw.replace(/[\s"'>),]+$/, '')
  try {
    const url = raw.includes('://') ? new URL(raw) : new URL(`https://${raw}`)
    if (url.hostname) raw = url.hostname
  } catch {
    raw = raw.split('/')[0]?.split('?')[0]?.split('#')[0] ?? raw
  }
  raw = raw.replace(/^www\./, '').replace(/\.+$/, '').replace(/:+\d+$/, '')
  raw = raw.replace(/[.,;:]+$/g, '')
  return raw
}

function resolveDomainSlugAlias(slug: string): string {
  const compact = slug.replace(/[-_]/g, '')
  const exact = DOMAIN_SLUG_ALIASES[slug] || DOMAIN_SLUG_ALIASES[compact]
  if (exact) return exact
  if (compact.length < 6) return slug
  const canonicals = [...new Set(Object.values(DOMAIN_SLUG_ALIASES))]
  let best: string | null = null
  let bestDist = Number.POSITIVE_INFINITY
  for (const canon of canonicals) {
    if (Math.abs(canon.length - compact.length) > 2) continue
    const dist = levenshtein(compact, canon)
    if (dist < bestDist) {
      bestDist = dist
      best = canon
    }
  }
  const maxDist = compact.length >= 10 ? 2 : 1
  if (best && bestDist > 0 && bestDist <= maxDist) return best
  return slug
}

/**
 * A research host is a DNS name. A company name, a space, or a percent-encoded
 * name (`fjordtest%20regnskap%20as`) is not a domain.
 */
export function isResearchDomainHost(host: string | null | undefined): boolean {
  const value = (host || '').trim().toLowerCase()
  if (!value || /[\s%]/.test(value) || value.includes('..')) return false
  return /^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}$/.test(value)
}

/** Normalize an email/web host for research (strip www / junk, apply slug aliases). */
export function normalizeResearchDomainHost(hostOrDomain: string): string {
  const raw = extractDomainHost(hostOrDomain)
  if (!raw) return ''
  const parts = raw.split('.').filter(Boolean)
  if (parts.length === 0) return ''
  const slug = parts[0] || ''
  const aliased = resolveDomainSlugAlias(slug)
  const host = aliased === slug ? raw : [aliased, ...parts.slice(1)].join('.')
  return isResearchDomainHost(host) ? host : ''
}

/** Company label from a typed http(s) URL host. Our site and non-hosts yield nothing. */
export function companyLabelFromTypedUrl(url: string | null | undefined): string | undefined {
  const value = url?.trim()
  if (!value || isOwnSiteUri(value)) return undefined
  let host = ''
  try {
    host = new URL(value).hostname.replace(/^www\./, '').toLowerCase()
  } catch {
    return undefined
  }
  if (!isResearchDomainHost(host)) return undefined
  const label = deriveCompanySearchLabel({ emailDomain: host, companyUrl: value })
  return label || undefined
}

/** Human label for a (possibly mistyped) domain — aliases first, then derive. */
export function normalizeCompanyDomainLabel(emailDomain: string, companyUrl?: string): string {
  const normalized = normalizeResearchDomainHost(emailDomain)
  return deriveCompanySearchLabel({
    emailDomain: normalized,
    ...(companyUrl ? { companyUrl } : {}),
  })
}

export interface ResearchLocationHint {
  country?: string
  city?: string
}

export interface ResearchRegionHints {
  regionContext: string
  registryTerm: string
  countryCode?: string
}

/** True when research returned a sentinel instead of a real company name. */
export function isPlaceholderCompanyName(name: string | null | undefined): boolean {
  if (!name?.trim()) return true
  return PLACEHOLDER_COMPANY_NAME_SET.has(name.trim().toLowerCase())
}

const CONSULTANT_IDENTITY_COMPANY_LABELS = new Set([
  'farzad bayat consulting',
  'f.b/c',
  'f.b / c',
  'fbc',
  'fbc lab',
  'bayat consulting',
])

function normalizeCompanyCompare(value: string): string {
  return value
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[^a-z0-9æøå]+/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

/** F.B/c / Bayat Consulting is the host, never the visitor company. */
export function isConsultantIdentityCompany(name: string | null | undefined): boolean {
  if (!name?.trim()) return false
  return CONSULTANT_IDENTITY_COMPANY_LABELS.has(normalizeCompanyCompare(name))
}

function compactCompanyToken(value: string): string {
  return value
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[^a-z0-9]/g, '')
}

/**
 * True when `name` is the email-domain token, including a trailing AS/ASA.
 * The rendered label is one word. The same letters with spaces are still
 * this fallback, so a chat-stated company can replace it.
 */
export function isEmailDerivedCompanyLabel(
  name: string | null | undefined,
  emailDomain?: string | null,
): boolean {
  if (!name?.trim() || !emailDomain?.trim()) return false
  const derived = deriveCompanySearchLabel({ emailDomain })
  const a = normalizeCompanyCompare(name)
  const b = normalizeCompanyCompare(derived)
  if (!a || !b) return false
  if (a === b) return true
  if (a === `${b} as` || a === `${b} asa` || b === `${a} as` || b === `${a} asa`) return true
  const host = normalizeCompanyCompare(emailDomain)
  const slug = normalizeCompanyCompare(emailDomain.split('.')[0] || '')
  if (a === host || a === slug) return true
  const nameCompact = compactCompanyToken(name)
  const slugCompact = compactCompanyToken(emailDomain.split('.')[0] || '')
  if (!nameCompact || !slugCompact) return false
  return (
    nameCompact === slugCompact ||
    nameCompact === `${slugCompact}as` ||
    nameCompact === `${slugCompact}asa`
  )
}

/**
 * Concierge `company` is either a URL/host or a human company name.
 * Do not wrap "Proff Rørlegger AS" as https://Proff Rørlegger AS.
 */
export function splitCompanyUrlOrName(value?: string | null): {
  companyUrl?: string
  statedCompany?: string
} {
  const trimmed = value?.trim()
  if (!trimmed) return {}
  if (/^https?:\/\//i.test(trimmed)) return { companyUrl: trimmed }
  if (!/\s/.test(trimmed) && /^[a-z0-9.-]+\.[a-z]{2,}$/i.test(trimmed)) {
    return { companyUrl: `https://${trimmed.replace(/^www\./, '')}` }
  }
  if (!isPlaceholderCompanyName(trimmed) && !isConsultantIdentityCompany(trimmed)) {
    return { statedCompany: trimmed }
  }
  return {}
}

export function shouldPromoteStatedCompany(
  stated: string | null | undefined,
  existing: string | null | undefined,
  emailDomain?: string | null,
): boolean {
  const next = stated?.trim()
  if (!next || isPlaceholderCompanyName(next) || isConsultantIdentityCompany(next)) return false
  if (!existing || isPlaceholderCompanyName(existing)) return true
  if (isConsultantIdentityCompany(existing)) return true
  if (isEmailDerivedCompanyLabel(existing, emailDomain)) return true
  return normalizeCompanyCompare(existing) === normalizeCompanyCompare(next)
}

const STATED_COMPANY_NAME =
  '([\\p{Lu}ÆØÅ][\\p{L}0-9&.\'’-]*(?:\\s+[\\p{Lu}ÆØÅ][\\p{L}0-9&.\'’-]*){0,5})'

const STATED_COMPANY_PATTERNS: RegExp[] = [
  new RegExp(`\\b(?:[Mm]y|[Oo]ur|[Tt]he)\\s+company\\s+is\\s+${STATED_COMPANY_NAME}`, 'u'),
  new RegExp(
    `\\b(?:[Ii] work (?:at|for)|[Ii](?:'m| am) with|[Jj]eg jobber (?:i|hos|for)|[Ss]elskapet heter|[Vv]i heter)\\s+${STATED_COMPANY_NAME}`,
    'u',
  ),
  new RegExp(
    `\\b(?:at|with|from|hos)\\s+${STATED_COMPANY_NAME}(?=\\s*(?:[.,;:]|\\b(?:we|we're|we are|and|where|who|that|is|og|som|hvor)\\b|$))`,
    'u',
  ),
]

function cleanStatedCompany(value: string): string {
  return value.replace(/\s+/g, ' ').replace(/[.,;:!?]+$/g, '').trim()
}

/** Chat / dossier stated company. Does not infer from email domain. */
export function extractStatedCompanyFromText(text: string): string | undefined {
  if (!text.trim()) return undefined
  for (const pattern of STATED_COMPANY_PATTERNS) {
    const match = text.match(pattern)
    const raw = match?.[1] ? cleanStatedCompany(match[1]) : ''
    if (
      raw &&
      raw.length >= 2 &&
      !isPlaceholderCompanyName(raw) &&
      !isConsultantIdentityCompany(raw)
    ) {
      return raw
    }
  }
  return undefined
}

/** Prefer researched name when it matches the email-domain org; never persist namesakes or placeholders. */
export function resolveCompanyNameForProjection(input: {
  researchName?: string | null | undefined
  intakeLabel?: string | null | undefined
  emailDomain?: string | null | undefined
  companyUrl?: string | null | undefined
  statedCompany?: string | null | undefined
}): string | undefined {
  const stated = input.statedCompany?.trim()
  if (stated && !isPlaceholderCompanyName(stated) && !isConsultantIdentityCompany(stated)) {
    return stated
  }

  const domain = input.emailDomain?.trim()
  const own = Boolean(domain && isOwnSiteDomain(domain))
  if (own) {
    const fromUrl = companyLabelFromTypedUrl(input.companyUrl)
    const intakeRaw = input.intakeLabel?.trim()
    const intake =
      intakeRaw && !isPlaceholderCompanyName(intakeRaw) && !isEmailDerivedCompanyLabel(intakeRaw, domain)
        ? intakeRaw
        : undefined
    const researchRaw = input.researchName?.trim()
    const research =
      researchRaw &&
      !isPlaceholderCompanyName(researchRaw) &&
      !isConsultantIdentityCompany(researchRaw) &&
      !isEmailDerivedCompanyLabel(researchRaw, domain)
        ? researchRaw
        : undefined
    // A typed URL on another host is the company. A stored name that is not
    // the email label stays, including when the bound domain is still our site.
    if (fromUrl) {
      if (research) {
        try {
          const host = new URL(input.companyUrl || '').hostname
          if (companyNameMatchesDomain(research, host)) return research
        } catch {
          // The URL host label below is the company name.
        }
      }
      return intake || fromUrl
    }
    return research || intake
  }

  const derived = domain
    ? deriveCompanySearchLabel({
        emailDomain: domain,
        ...(input.companyUrl ? { companyUrl: input.companyUrl } : {}),
      })
    : undefined
  const intake = input.intakeLabel?.trim()
  const intakeLabel = intake && !isPlaceholderCompanyName(intake) ? intake : derived

  const research = input.researchName?.trim()
  if (research && !isPlaceholderCompanyName(research)) {
    if (!domain || extractedCompanyAlignsWithVisitor(research, domain, intakeLabel, input.companyUrl)) {
      return research
    }
  }

  if (intakeLabel) return intakeLabel
  return derived
}

/**
 * COMPANY label + research target: chat/dossier stated company wins over email-domain fallback.
 * Email domain remains a person search key, not the company to quote once they named one.
 */
export function resolveVisitorFacingCompanyName(input: {
  statedCompany?: string | null | undefined
  personCompany?: string | null | undefined
  researchName?: string | null | undefined
  intakeLabel?: string | null | undefined
  projectionName?: string | null | undefined
  emailDomain?: string | null | undefined
  companyUrl?: string | null | undefined
}): string | undefined {
  const statedCandidates = [input.statedCompany, input.projectionName, input.personCompany]
  for (const candidate of statedCandidates) {
    const value = candidate?.trim()
    if (!value || isPlaceholderCompanyName(value) || isConsultantIdentityCompany(value)) continue
    if (isEmailDerivedCompanyLabel(value, input.emailDomain)) continue
    return value
  }

  return resolveCompanyNameForProjection({
    researchName: input.researchName,
    intakeLabel: input.intakeLabel || input.projectionName,
    emailDomain: input.emailDomain,
    companyUrl: input.companyUrl,
    statedCompany: input.statedCompany,
  })
}

/** Turn a domain token into a company label. One DNS label stays one word. */
export function deriveCompanySearchLabel(input: {
  emailDomain: string
  companyUrl?: string
  explicitCompany?: string
}): string {
  const explicit = input.explicitCompany?.trim()
  if (explicit) return explicit

  const normalizedEmailHost = normalizeResearchDomainHost(input.emailDomain)
  let host = normalizedEmailHost.replace(/^www\./, '').split('.')[0] || ''
  if (input.companyUrl) {
    try {
      const urlHost = new URL(input.companyUrl).hostname
      host = normalizeResearchDomainHost(urlHost).replace(/^www\./, '').split('.')[0] || host
    } catch {
      // keep email domain host
    }
  }

  // Apply alias on the bare slug too (bhakedemiet → bhakademiet)
  const aliasedSlug = DOMAIN_SLUG_ALIASES[host.toLowerCase()] || host
  host = aliasedSlug

  const slug = host.replace(/[-_]+/g, ' ').trim()
  if (!slug) return input.emailDomain

  // A single DNS label stays one word. Hyphens and underscores are the only separators.
  // Do not split on "to", "and", or other dictionary pieces: kulturdirektoratet
  // is not "Kulturdirek To Ratet".
  const spaced = slug
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim()

  return spaced
    .split(' ')
    .filter(Boolean)
    .map((word, index) => {
      const lower = word.toLowerCase()
      if (lower === 'mr' || lower === 'ms' || lower === 'dr') {
        return lower.charAt(0).toUpperCase() + lower.slice(1)
      }
      // Short 2-letter brand prefix: bh → BH (BH Akademiet)
      if (index === 0 && /^[a-z]{2}$/i.test(word)) {
        return word.toUpperCase()
      }
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    })
    .join(' ')
}

export function inferResearchRegionHints(
  email: string,
  location?: ResearchLocationHint,
  companyLabel?: string
): ResearchRegionHints {
  const tld = email.split('.').pop()?.toLowerCase() ?? ''
  const country = (location?.country || '').toUpperCase().trim()
  const city = (location?.city || '').trim()
  const label = (companyLabel || '').toLowerCase()

  const isNorway =
    tld === 'no' ||
    country === 'NO' ||
    country === 'NORWAY' ||
    country === 'NORGE' ||
    /\b(oslo|bergen|trondheim|stavanger)\b/i.test(city) ||
    /\b(as|asa)\b/i.test(label)

  if (isNorway) {
    return {
      regionContext: [city || 'Oslo', 'Norway'].filter(Boolean).join(' '),
      registryTerm: 'brreg proff.no firmadatabasen.no',
      countryCode: 'NO',
    }
  }

  if (tld === 'ae' || country === 'AE' || country === 'UAE' || country === 'UNITED ARAB EMIRATES') {
    return {
      regionContext: [city || 'Dubai', 'UAE'].filter(Boolean).join(' '),
      registryTerm: 'DIFC OR DED license',
      countryCode: 'AE',
    }
  }

  if (tld === 'uk' || country === 'GB' || country === 'UK' || country === 'UNITED KINGDOM') {
    return {
      regionContext: [city || 'London', 'UK'].filter(Boolean).join(' '),
      registryTerm: 'Companies House',
      countryCode: 'GB',
    }
  }

  if (country === 'US' || country === 'USA' || country === 'UNITED STATES') {
    return {
      regionContext: [city, 'United States'].filter(Boolean).join(' '),
      registryTerm: 'SEC state business registry',
      countryCode: 'US',
    }
  }

  if (city || location?.country) {
    return {
      regionContext: [city, location?.country].filter(Boolean).join(' '),
      registryTerm: 'business registry',
    }
  }

  return { regionContext: '', registryTerm: 'business registry' }
}

function uniqueQueries(queries: string[]): string[] {
  const seen = new Set<string>()
  const out: string[] = []
  for (const query of queries) {
    const trimmed = query.replace(/\s+/g, ' ').trim()
    if (!trimmed || seen.has(trimmed)) continue
    seen.add(trimmed)
    out.push(trimmed)
  }
  return out
}

/** Google-style queries — name + email + domain + location when present. */
export function buildLeadResearchQueries(input: {
  personName: string
  companyLabel: string
  email: string
  location?: ResearchLocationHint
}): string[] {
  const { personName, companyLabel, email, location } = input
  const region = inferResearchRegionHints(email, location, companyLabel)

  const domain = (email.split('@')[1]?.toLowerCase().trim() || '').replace(/\.+$/, '')
  // Our own domain is the test inbox, not the lead's firm. Name, typed company, and region only.
  const ownSite = isOwnSiteDomain(domain)
  const anchorEmail = ownSite ? '' : email
  const anchorDomain = ownSite ? '' : domain
  const locationHint = [location?.city, location?.country].filter(Boolean).join(' ')
  const anchors = [personName ? `"${personName}"` : '', anchorEmail, anchorDomain, locationHint]
    .filter(Boolean)
    .join(' ')

  const primary = anchors
  const companyQ = `"${personName}" "${companyLabel}" ${anchorEmail} ${anchorDomain} ${locationHint}`.trim()
  const linkedIn = `"${personName}" ${anchorEmail} ${anchorDomain} site:linkedin.com/in`
  const registry = region.regionContext
    ? `"${personName}" "${companyLabel}" ${anchorEmail} ${anchorDomain} ${region.regionContext} ${region.registryTerm}`
    : `"${personName}" "${companyLabel}" ${anchorEmail} ${anchorDomain} headquarters location ${region.registryTerm}`
  const currentWork = `"${personName}" ${anchorEmail} ${anchorDomain} ${locationHint} current role LinkedIn`.trim()
  const companyIntel = `"${personName}" "${companyLabel}" ${anchorEmail} ${anchorDomain} founder CEO daglig leder`.trim()
  const statedCompanyFirst =
    companyLabel && domain && !isEmailDerivedCompanyLabel(companyLabel, domain)
      ? `"${companyLabel}" ${region.registryTerm} orgnr adresse telefon`.trim()
      : ''

  return uniqueQueries([
    statedCompanyFirst,
    primary,
    companyQ,
    linkedIn,
    registry,
    currentWork,
    companyIntel,
  ])
}
